import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useConfig, useFeature, usePlans, useText } from '@/core/config/ConfigProvider';
import { registerSection, type SectionComponentProps } from '@/core/sections/section-registry';
import { destaquesDoPlano } from '@/core/plans/destaques';
import { Badge, Button, ButtonLink, Card, Container, Input, SectionHeading, cx } from '@/design-system';
import { formatCents } from '@/core/format/br';
import { ListingGrid, ListingGridSkeleton } from '@/modules/catalog/ListingCardView';
import { getFeaturedCities, getFeaturedListings, getRecentListings } from '@/modules/catalog/api';
import { HeroSearch } from './HeroSearch';

/**
 * SEÇÕES DA HOME (Cap. 5)
 *
 * Cada componente lê seus parâmetros de `section.props`, definidos no painel.
 * "Mostrar 8 destaques" é um número na configuração, não um literal aqui.
 */

/** Casca padrão: respiro vertical e cabeçalho consistentes em toda seção. */
function Band({
  children,
  tone = 'canvas',
  className,
}: {
  children: ReactNode;
  tone?: 'canvas' | 'surface';
  className?: string;
}) {
  return (
    <section className={cx('py-14 md:py-16', tone === 'surface' && 'bg-surface', className)}>
      <Container>
        <div className="flex flex-col gap-8">{children}</div>
      </Container>
    </section>
  );
}

// ------------------------------------------------- busca em linguagem natural

/** "QUAL IMÓVEL DESEJA?" — o diferencial declarado no Cap. 5.2. */
function NaturalLanguageSearch({ section }: SectionComponentProps) {
  const { search } = useConfig();
  const t = useText();
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const config = search.naturalLanguage;

  if (!config.enabled) return null;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const phrase = value.trim();
    if (!phrase) return;
    // A frase inteira vai para a busca: o backend interpreta, devolve os
    // filtros entendidos e guarda como procura para gerar match depois.
    navigate(`/lotes?q=${encodeURIComponent(phrase)}`);
  };

  return (
    <Band tone="surface">
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-6 text-center">
        <div className="flex flex-col gap-2">
          <span className="text-xs font-bold uppercase tracking-[0.14em] text-primary">Busca inteligente</span>
          <h2 className="font-display text-2xl font-extrabold text-ink md:text-4xl">
            {section.title ?? t('home.nls.title', undefined, 'QUAL IMÓVEL DESEJA?')}
          </h2>
          <p className="text-pretty text-ink-soft">
            {section.subtitle ?? t('home.nls.subtitle', undefined, 'Escreva do seu jeito.')}
          </p>
        </div>

        <form onSubmit={submit} className="flex w-full flex-col gap-2 sm:flex-row">
          <Input
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={config.placeholder}
            aria-label="Descreva o imóvel que você procura"
            className="h-13 flex-1 text-[0.9375rem]"
          />
          <Button type="submit" size="lg">
            {t('home.nls.cta', undefined, 'Buscar')}
          </Button>
        </form>

        {config.examples.length > 0 ? (
          <div className="flex flex-wrap justify-center gap-2">
            {config.examples.map((example) => (
              <button key={example} type="button" className="ds-choice" onClick={() => setValue(example)}>
                {example}
              </button>
            ))}
          </div>
        ) : null}

        <p className="text-sm text-ink-soft">
          Não achou agora? Guardamos sua procura e avisamos assim que entrar um imóvel compatível.
        </p>
      </div>
    </Band>
  );
}

// -------------------------------------------------------------- listagens

function listingsSection(kind: 'featured' | 'recent') {
  return function Section({ section }: SectionComponentProps) {
    const limit = Number(section.props.limit ?? 8);
    const query = useQuery({
      queryKey: ['home-listings', kind],
      queryFn: kind === 'featured' ? getFeaturedListings : getRecentListings,
    });

    return (
      <Band tone={kind === 'featured' ? 'canvas' : 'surface'}>
        <SectionHeading
          eyebrow={kind === 'featured' ? 'Destaques' : 'Novidades'}
          title={section.title ?? (kind === 'featured' ? 'Imóveis em destaque' : 'Adicionados recentemente')}
          subtitle={section.subtitle}
          action={
            <Link to="/lotes" className="text-sm font-semibold text-primary no-underline hover:underline">
              Ver todos os imóveis →
            </Link>
          }
        />

        {query.isLoading ? (
          <ListingGridSkeleton count={4} />
        ) : (
          <ListingGrid listings={(query.data ?? []).slice(0, limit)} />
        )}
      </Band>
    );
  };
}

// ------------------------------------------------------------ localidades

function FeaturedCities({ section }: SectionComponentProps) {
  const query = useQuery({ queryKey: ['featured-cities'], queryFn: getFeaturedCities });

  return (
    <Band tone="surface">
      <SectionHeading
        eyebrow="Onde procurar"
        title={section.title ?? 'Cidades com mais oportunidades'}
        subtitle={section.subtitle ?? 'Navegue pelas praças onde há mais anúncios ativos agora.'}
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {(query.data ?? []).map((city) => (
          <Link
            key={city.value}
            to={`/lotes?cidade=${encodeURIComponent(city.label)}`}
            className={cx(
              'flex items-center justify-between gap-2 rounded-(--radius-control) border border-line bg-surface',
              'px-4 py-3 no-underline transition-colors hover:border-primary hover:bg-primary-soft',
            )}
          >
            <span className="flex flex-col">
              <span className="font-semibold text-ink">{city.label}</span>
              <span className="text-xs text-ink-faint">{city.state}</span>
            </span>
            <span className="tnum text-sm font-semibold text-primary">{city.listingCount}</span>
          </Link>
        ))}
      </div>
    </Band>
  );
}

/** As "categorias" são a taxonomia de aptidão — trocável pelo painel. */
function Categories({ section }: SectionComponentProps) {
  const { taxonomies } = useConfig();
  const taxonomyKey = String(section.props.taxonomy ?? 'aptidao');
  const options = (taxonomies[taxonomyKey] ?? []).filter((option) => option.enabled);

  return (
    <Band>
      <SectionHeading
        eyebrow="Por finalidade"
        title={section.title ?? 'O que você vai fazer com o terreno?'}
        subtitle={section.subtitle}
      />

      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <Link key={option.value} to={`/lotes?aptidao=${option.value}`} className="ds-choice">
            {option.label}
          </Link>
        ))}
      </div>
    </Band>
  );
}

// ------------------------------------------------------------------ planos

/** Vitrine resumida dos planos. A página completa fica em /planos. */
function PlansShowcase({ section }: SectionComponentProps) {
  const plans = usePlans();
  const t = useText();
  // Enquanto a cobranca nao esta ligada, o card leva para a pagina de planos
  // em vez de um checkout que ainda nao existe.
  const checkoutEnabled = useFeature('checkout');
  const featured = plans.filter((plan) => plan.publication.enabled).slice(0, 4);

  return (
    <Band tone="surface">
      <SectionHeading
        eyebrow="Para quem vende"
        title={section.title ?? t('plans.title', undefined, 'Planos para anunciar')}
        subtitle={section.subtitle ?? 'Comece de graça. Suba de plano quando o volume pedir.'}
        action={
          <Link to="/planos" className="text-sm font-semibold text-primary no-underline hover:underline">
            Comparar planos →
          </Link>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {featured.map((plan) => {
          const price = plan.prices[0];
          const isFree = !price || price.amount === 0;

          return (
            <Card
              key={plan.id}
              className={cx('flex flex-col gap-4 p-5', plan.highlighted && 'border-primary ring-1 ring-primary')}
            >
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-display text-lg font-bold text-ink">{plan.name}</h3>
                {plan.badge ? <Badge tone="accent">{plan.badge}</Badge> : null}
              </div>

              <p className="tnum font-display text-2xl font-extrabold text-ink">
                {isFree ? 'Grátis' : formatCents(price.amount)}
                {price && !isFree ? <span className="text-sm font-medium text-ink-faint">/mês</span> : null}
              </p>

              <ul className="flex flex-1 flex-col gap-1.5 text-sm text-ink-soft">
                {destaquesDoPlano(plan.limits).slice(0, 4).map((benefit) => (
                  <li key={benefit} className="flex gap-2">
                    <span aria-hidden="true" className="text-primary">
                      ✓
                    </span>
                    {benefit}
                  </li>
                ))}
              </ul>

              <ButtonLink
                href={isFree ? '/anunciar' : checkoutEnabled ? `/checkout?plano=${plan.slug}` : '/planos'}
                variant={plan.highlighted ? 'primary' : 'outline'}
                block
              >
                {isFree ? 'Começar grátis' : t('plans.cta', undefined, 'Assinar')}
              </ButtonLink>
            </Card>
          );
        })}
      </div>
    </Band>
  );
}

// -------------------------------------------------------- captação de anúncio

function AdvertiserCta({ section }: SectionComponentProps) {
  const bullets = (section.props.bullets ?? []) as string[];

  return (
    <section className="py-14 md:py-16">
      <Container>
        <div className="overflow-hidden rounded-(--radius-card) border border-line bg-primary-soft">
          <div className="flex flex-col gap-6 p-8 md:flex-row md:items-center md:justify-between md:p-12">
            <div className="flex max-w-xl flex-col gap-3">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-primary">Anuncie</span>
              <h2 className="font-display text-2xl font-extrabold text-ink md:text-3xl">
                {section.title ?? 'Tem um lote para vender?'}
              </h2>
              {section.subtitle ? <p className="text-pretty text-ink-soft">{section.subtitle}</p> : null}

              {bullets.length > 0 ? (
                <ul className="flex flex-col gap-1.5 text-sm text-ink-soft">
                  {bullets.map((bullet) => (
                    <li key={bullet} className="flex gap-2">
                      <span aria-hidden="true" className="text-primary">
                        ✓
                      </span>
                      {bullet}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            <div className="flex shrink-0 flex-col gap-2 sm:flex-row md:flex-col">
              <ButtonLink href="/anunciar" size="lg">
                {String(section.props.label ?? 'Anunciar grátis')}
              </ButtonLink>
              <ButtonLink href="/planos" variant="outline" size="lg">
                Ver planos
              </ButtonLink>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

// -------------------------------------------------------------------- apoio

function Faq({ section }: SectionComponentProps) {
  const items = (section.props.items ?? []) as { question: string; answer: string }[];

  return (
    <Band tone="surface">
      <SectionHeading eyebrow="Dúvidas" title={section.title ?? 'Perguntas frequentes'} subtitle={section.subtitle} />

      <div className="mx-auto flex w-full max-w-3xl flex-col gap-3">
        {items.map((item) => (
          <details
            key={item.question}
            className="group rounded-(--radius-control) border border-line bg-canvas px-5 py-4 open:bg-surface"
          >
            <summary className="flex cursor-pointer items-center justify-between gap-4 font-semibold text-ink marker:content-['']">
              {item.question}
              <span aria-hidden="true" className="text-primary transition-transform group-open:rotate-45">
                +
              </span>
            </summary>
            <p className="mt-3 text-pretty text-[0.9375rem] leading-relaxed text-ink-soft">{item.answer}</p>
          </details>
        ))}
      </div>
    </Band>
  );
}

function ContentCards({ section }: SectionComponentProps) {
  const items = (section.props.items ?? []) as { title: string; text: string; href?: string }[];

  return (
    <Band>
      <SectionHeading eyebrow="Conteúdo" title={section.title ?? 'Antes de fechar negócio'} subtitle={section.subtitle} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <Card key={item.title} className="flex flex-col gap-2 p-5">
            <h3 className="font-display text-lg font-bold text-ink">{item.title}</h3>
            <p className="flex-1 text-sm leading-relaxed text-ink-soft">{item.text}</p>
            {item.href ? (
              <Link to={item.href} className="text-sm font-semibold text-primary no-underline hover:underline">
                Saiba mais →
              </Link>
            ) : null}
          </Card>
        ))}
      </div>
    </Band>
  );
}

function Stats({ section }: SectionComponentProps) {
  const items = (section.props.items ?? []) as { label: string; value: string }[];

  return (
    <Band>
      <dl className="grid grid-cols-2 gap-6 md:grid-cols-4">
        {items.map((item) => (
          <div key={item.label} className="flex flex-col gap-1">
            <dt className="sr-only">{item.label}</dt>
            <dd className="tnum font-display text-3xl font-extrabold text-primary">{item.value}</dd>
            <span className="text-sm text-ink-soft">{item.label}</span>
          </div>
        ))}
      </dl>
    </Band>
  );
}

function BannerSection({ section }: SectionComponentProps) {
  const { site } = useConfig();
  const slot = String(section.props.slot ?? '');
  const banner = site.banners.find((candidate) => candidate.slot === slot && candidate.publication.enabled);
  if (!banner) return null;

  const image = (
    <img
      src={banner.imageUrl}
      alt={banner.alt}
      loading="lazy"
      className="w-full rounded-(--radius-card) border border-line"
    />
  );

  return (
    <section className="py-8">
      <Container>
        {banner.href ? (
          <a href={banner.href} target="_blank" rel="noreferrer noopener sponsored">
            {image}
          </a>
        ) : (
          image
        )}
      </Container>
    </section>
  );
}

function CallToAction({ section }: SectionComponentProps) {
  return (
    <Band>
      <div className="flex flex-col items-start gap-4">
        <SectionHeading title={section.title ?? ''} subtitle={section.subtitle} />
        <ButtonLink href={String(section.props.href ?? '/anunciar')} size="lg">
          {String(section.props.label ?? 'Anunciar')}
        </ButtonLink>
      </div>
    </Band>
  );
}

/**
 * Bloco de HTML editado no painel. O conteúdo é sanitizado no BACKEND, ao
 * salvar, com allowlist de tags — nunca aqui, onde qualquer bypass do
 * cliente já seria XSS.
 */
function RawHtml({ section }: SectionComponentProps) {
  const html = String(section.props.html ?? '');

  return (
    <Band>
      <div
        className="prose-solote max-w-3xl text-ink-soft [&_a]:text-primary [&_h2]:text-ink [&_h3]:text-ink"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </Band>
  );
}

let registered = false;

export function registerHomeSections(): void {
  if (registered) return;
  registered = true;

  registerSection('hero_search', HeroSearch);
  registerSection('natural_language_search', NaturalLanguageSearch);
  registerSection('featured_listings', listingsSection('featured'));
  registerSection('recent_listings', listingsSection('recent'));
  registerSection('featured_cities', FeaturedCities);
  registerSection('categories', Categories);
  registerSection('plans_showcase', PlansShowcase);
  registerSection('advertiser_cta', AdvertiserCta);
  registerSection('banner', BannerSection);
  registerSection('content_cards', ContentCards);
  registerSection('cta', CallToAction);
  registerSection('stats', Stats);
  registerSection('faq', Faq);
  registerSection('html', RawHtml);
}
